import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'recentlyViewedProducts';
const MAX_ITEMS = 4;

export const useRecentlyViewed = () => {
  const [recentlyViewedHandles, setRecentlyViewedHandles] = useState<string[]>([]);

  // Load from localStorage on mount (and normalize legacy formats)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const parsed = JSON.parse(stored) as unknown;
      if (!Array.isArray(parsed)) return;

      const handles = parsed
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            const maybeHandle = (item as any).handle ?? (item as any).product?.handle;
            return typeof maybeHandle === 'string' ? maybeHandle : null;
          }
          return null;
        })
        .filter((h): h is string => typeof h === 'string' && h.length > 0);

      const normalized = Array.from(new Set(handles)).slice(0, MAX_ITEMS);
      setRecentlyViewedHandles(normalized);

      // write back normalized format
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
    }
  }, []);

  const addProductHandle = useCallback((handle: string) => {
    setRecentlyViewedHandles((prev) => {
      // Remove if already exists
      const filtered = prev.filter((h) => h !== handle);
      // Add to beginning
      const updated = [handle, ...filtered].slice(0, MAX_ITEMS);
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving recently viewed products:', error);
      }
      return updated;
    });
  }, []);

  const getRecentlyViewedHandles = useCallback((excludeHandle?: string): string[] => {
    if (excludeHandle) {
      return recentlyViewedHandles.filter((h) => h !== excludeHandle);
    }
    return recentlyViewedHandles;
  }, [recentlyViewedHandles]);

  return { recentlyViewedHandles, addProductHandle, getRecentlyViewedHandles };
};
